import { observer } from 'mobx-react-lite';
import { FC, ReactElement } from 'react';

import { GROUPS_PLACEHOLDER_ITEM_SIZE, GroupsPlaceholder } from '@gem/components';
import { useTranslation } from '@gem/modules/i18n';

import { VirtualizedList } from 'APP/components/VirtualizedList/VirtualizedList';

import styles from './SearchResult.styles.m.css';
import { SearchResultEmpty } from './SearchResultEmpty/SearchResultEmpty';
import { SearchResultInfo } from './SearchResultInfo/SearchResultInfo';
import { SearchResultItem } from './SearchResultItem/SearchResultItem';
import { isDelimiterItem, useSearchResultPresenter } from './presenter/SearchResult.presenter';

const PLACEHOLDER_ITEMS_COUNT = 5;

const DELIMITER_ITEM_HEIGHT = 56;
const RESULT_ITEM_HEIGHT = 64;

interface ISearchResultProps {
  searchText: string;
}

export const SearchResult: FC<ISearchResultProps> = observer(({ searchText }) => {
  const presenter = useSearchResultPresenter(searchText);
  const { t } = useTranslation();

  if (presenter.showEmptyNote) {
    return <SearchResultInfo />;
  }

  if (presenter.showNoResults) {
    return <SearchResultEmpty />;
  }

  const getItemSize = (index: number): number => {
    const item = presenter.results[index];

    if (isDelimiterItem(item)) {
      return DELIMITER_ITEM_HEIGHT;
    }

    return RESULT_ITEM_HEIGHT;
  };

  const getListItem = (index: number): ReactElement => {
    const item = presenter.results[index];

    if (isDelimiterItem(item)) {
      return <div className={styles.resultsDelimiter}>{t('extended_search_title')}</div>;
    }

    return (
      <SearchResultItem
        entity={item}
        isLocalResult={index < presenter.localResultsCount}
      />
    );
  };

  return (
    <VirtualizedList
      itemCount={presenter.results.length}
      getListItem={getListItem}
      itemSize={getItemSize}
      isShowLoader={presenter.isLoading}
      Loader={<GroupsPlaceholder count={PLACEHOLDER_ITEMS_COUNT} />}
      loaderSize={PLACEHOLDER_ITEMS_COUNT * GROUPS_PLACEHOLDER_ITEM_SIZE}
      hasMoreItems={presenter.showLoadMore}
      onLoadMore={presenter.loadMore}
    />
  );
});
